import { useQuery } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";

import type {
  UserDto,
  WunderbricksAopTicketStatusOptionDto,
  WunderbricksProjectConfigurationDto,
  WunderbricksTicketStatusOptionsDto,
} from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormDatePicker } from "@/components/Form/FormDatePicker";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { Accordion } from "@/design-system/Accordion";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { Notice } from "@/design-system/Notice";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { createRequiredStringRule } from "@/helpers/rules";
import { usePageEntryState } from "@/hooks/usePageEntryState";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export type WunderbricksFormValues = {
  contractsEnabled: boolean;
  ticketsEnabled: boolean;
  autoSendTickets: boolean;
  instanceUrl: string;
  clientId: string;
  clientSecret: string;
  requesterUser: UserDto | undefined;
  syncContractsFromDate: Date | undefined;
  statusMappings: Record<string, WunderbricksAopTicketStatusOptionDto | undefined>;
};

// External status values can contain dots, which React Hook Form interprets as nested paths.
function statusMappingKey(externalStatus: string): string {
  return `status_${encodeURIComponent(externalStatus).replaceAll(".", "%2E")}`;
}

export function WunderbricksPage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const canManageWunderbricks = hasPermission((x) => x.integrations.canManageWunderbricks);
  const integrationQueries = useIntegrationQueries();
  const userQueries = useUserQueries();

  const {
    data: config,
    isFetching: isLoadingConfig,
    error: configError,
  } = useQuery({ ...integrationQueries.wunderbricksConfiguration(), enabled: canManageWunderbricks });
  const {
    data: adminUsers,
    isFetching: isLoadingAdminUsers,
    error: adminUsersError,
  } = useQuery({ ...userQueries.listAdmins(), enabled: canManageWunderbricks });
  const hasSavedTicketConnection = Boolean(
    config?.ticketsEnabled && config.instanceUrl && config.clientId && config.clientSecret,
  );
  const ticketStatusOptionsQuery = useQuery({
    ...integrationQueries.wunderbricksTicketStatusOptions(),
    enabled: canManageWunderbricks && hasSavedTicketConnection,
    retry: false,
  });
  const ticketStatusOptions = ticketStatusOptionsQuery.data;
  const mutation = integrationMutations.useEditWunderbricksConfiguration();
  const pageEntry = usePageEntryState({
    identity: slug,
    isFetching: isLoadingConfig || isLoadingAdminUsers,
    isReady: config !== undefined && adminUsers !== undefined,
    error: configError || adminUsersError,
  });

  if (!canManageWunderbricks) {
    return <ErrorPage status={404} />;
  }

  if (pageEntry.error) {
    return <ErrorPage error={pageEntry.error} />;
  }

  if (pageEntry.isPending) {
    return <FullSizeLoader withPadding />;
  }

  const name = t("page.integration.wunderbricks.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <WunderbricksForm
        key={slug}
        defaultValues={{
          contractsEnabled: config?.contractsEnabled || false,
          ticketsEnabled: config?.ticketsEnabled || false,
          autoSendTickets: config?.autoSendTickets || false,
          instanceUrl: config?.instanceUrl || "",
          clientId: config?.clientId || "",
          clientSecret: config?.clientSecret || "",
          requesterUser:
            !config?.requesterUserId || !adminUsers
              ? undefined
              : adminUsers.find((u) => u.id === config.requesterUserId),
          syncContractsFromDate: config?.syncContractsFromDate ? new Date(config.syncContractsFromDate) : undefined,
          statusMappings: Object.fromEntries(
            (ticketStatusOptions?.externalStatuses ?? []).map((externalStatus) => {
              const mapping = config?.statusMappings?.find((m) => m.externalStatus === externalStatus.value);

              return [
                statusMappingKey(externalStatus.value),
                mapping ? ticketStatusOptions?.aopStatuses.find((s) => s.id === mapping.ticketStatusId) : undefined,
              ];
            }),
          ),
        }}
        adminUsers={adminUsers || []}
        ticketStatusOptions={ticketStatusOptions || { aopStatuses: [], externalStatuses: [] }}
        hasSavedTicketConnection={hasSavedTicketConnection}
        isLoadingTicketStatusOptions={ticketStatusOptionsQuery.isFetching}
        hasTicketStatusOptionsError={ticketStatusOptionsQuery.isError}
        onRetryTicketStatusOptions={() => void ticketStatusOptionsQuery.refetch()}
        isSubmitting={mutation.isPending}
        onSubmit={(data, onSaved) => mutation.mutate(data, { onSuccess: onSaved })}
      />
    </DocumentPage>
  );
}

type WunderbricksFormProps = {
  defaultValues: Partial<WunderbricksFormValues>;
  ticketStatusOptions: WunderbricksTicketStatusOptionsDto;
  hasSavedTicketConnection: boolean;
  isLoadingTicketStatusOptions: boolean;
  hasTicketStatusOptionsError: boolean;
  onRetryTicketStatusOptions: () => void;
  adminUsers: UserDto[];
  isSubmitting: boolean;
  onSubmit: (data: WunderbricksProjectConfigurationDto, onSaved: () => void) => void;
};

function WunderbricksForm({
  adminUsers,
  defaultValues,
  ticketStatusOptions,
  hasSavedTicketConnection,
  isLoadingTicketStatusOptions,
  hasTicketStatusOptionsError,
  onRetryTicketStatusOptions,
  isSubmitting,
  onSubmit,
}: WunderbricksFormProps): React.ReactNode {
  const { t } = useTranslation();
  const formMethods = useForm<WunderbricksFormValues>({
    values: {
      contractsEnabled: false,
      ticketsEnabled: false,
      autoSendTickets: false,
      instanceUrl: "",
      clientId: "",
      clientSecret: "",
      requesterUser: undefined,
      syncContractsFromDate: undefined,
      ...defaultValues,
      statusMappings: defaultValues.statusMappings ?? {},
    },
    resetOptions: { keepDirtyValues: true },
  });

  const currTicketEnabled = useWatch({ control: formMethods.control, name: "ticketsEnabled" });
  const currContractEnabled = useWatch({ control: formMethods.control, name: "contractsEnabled" });
  const anyEnabled = currTicketEnabled || currContractEnabled;
  const [instanceUrl, clientId, clientSecret] = useWatch({
    control: formMethods.control,
    name: ["instanceUrl", "clientId", "clientSecret"],
  });
  const isUsingSavedConnection =
    hasSavedTicketConnection &&
    instanceUrl.trim() === (defaultValues.instanceUrl ?? "").trim() &&
    clientId.trim() === (defaultValues.clientId ?? "").trim() &&
    clientSecret.trim() === (defaultValues.clientSecret ?? "").trim();
  const canEditTicketStatusMapping =
    currTicketEnabled &&
    isUsingSavedConnection &&
    !isLoadingTicketStatusOptions &&
    !hasTicketStatusOptionsError &&
    ticketStatusOptions.aopStatuses.length > 0 &&
    ticketStatusOptions.externalStatuses.length > 0;

  const handleSubmit = (formValues: WunderbricksFormValues) => {
    const payload: WunderbricksProjectConfigurationDto = {
      contractsEnabled: formValues.contractsEnabled,
      ticketsEnabled: formValues.ticketsEnabled,
      autoSendTickets: formValues.autoSendTickets,
      instanceUrl: formValues.instanceUrl.trim() || undefined,
      clientId: formValues.clientId.trim() || undefined,
      clientSecret: formValues.clientSecret.trim() || undefined,
      requesterUserId: formValues.requesterUser?.id,
      syncContractsFromDate: formValues.syncContractsFromDate?.toISOString(),
    };

    if (canEditTicketStatusMapping) {
      payload.statusMappings = ticketStatusOptions.externalStatuses
        .map((externalTicketStatus) => ({
          externalStatus: externalTicketStatus.value,
          aopStatus: formValues.statusMappings[statusMappingKey(externalTicketStatus.value)],
        }))
        .flatMap((mapping) =>
          mapping.aopStatus ? [{ externalStatus: mapping.externalStatus, ticketStatusId: mapping.aopStatus.id }] : [],
        );
    }

    onSubmit(payload, () => {
      const currentValues = formMethods.getValues();
      if (payload.statusMappings !== undefined) {
        formMethods.reset(currentValues);

        return;
      }

      // Unavailable status options must not mark unsaved mapping edits as saved.
      for (const field of [
        "contractsEnabled",
        "ticketsEnabled",
        "autoSendTickets",
        "instanceUrl",
        "clientId",
        "clientSecret",
        "requesterUser",
        "syncContractsFromDate",
      ] as const) {
        formMethods.resetField(field, { defaultValue: currentValues[field] });
      }
    });
  };

  let statusMappingFeedback: React.ReactNode = null;
  if (!isUsingSavedConnection) {
    statusMappingFeedback = (
      <Text as="p" variant="caption" tone="secondary">
        {t("page.integration.wunderbricks.form.status-mappings.credentials-required")}
      </Text>
    );
  } else if (isLoadingTicketStatusOptions) {
    statusMappingFeedback = (
      <Text as="p" variant="caption" tone="secondary">
        {t("page.integration.wunderbricks.form.status-mappings.loading")}
      </Text>
    );
  } else if (hasTicketStatusOptionsError) {
    statusMappingFeedback = (
      <Notice
        variant="danger"
        message={t("page.integration.wunderbricks.form.status-mappings.error")}
        action={{ label: t("common.action.reload"), onClick: onRetryTicketStatusOptions }}
      />
    );
  } else if (!canEditTicketStatusMapping) {
    statusMappingFeedback = (
      <Notice
        variant="warning"
        message={t("page.integration.wunderbricks.form.status-mappings.empty")}
        action={{ label: t("common.action.reload"), onClick: onRetryTicketStatusOptions }}
      />
    );
  }

  return (
    <Form.Root formMethods={formMethods} onSubmit={handleSubmit}>
      <Form.Container title={t("page.integration.wunderbricks.title")}>
        <Form.Body>
          <FormCheckbox<WunderbricksFormValues, "ticketsEnabled">
            id="ticketsEnabled"
            name="ticketsEnabled"
            label={t("page.integration.wunderbricks.form.tickets-enabled")}
            disabled={isSubmitting}
            onChange={(e) => {
              if (e.target.checked) {
                formMethods.setValue("contractsEnabled", true, { shouldDirty: true });
              } else {
                formMethods.setValue("autoSendTickets", false);
              }
            }}
          />

          <FormCheckbox<WunderbricksFormValues, "autoSendTickets">
            id="autoSendTickets"
            name="autoSendTickets"
            label={t("page.integration.wunderbricks.form.auto-send-tickets")}
            disabled={isSubmitting || !currTicketEnabled}
          />

          <FormCheckbox<WunderbricksFormValues, "contractsEnabled">
            id="contractsEnabled"
            name="contractsEnabled"
            label={t("page.integration.wunderbricks.form.contracts-enabled")}
            disabled={isSubmitting || currTicketEnabled}
          />

          <FormField label={t("page.integration.wunderbricks.form.sync-contracts-from-date")}>
            <FormDatePicker<WunderbricksFormValues, "syncContractsFromDate">
              name="syncContractsFromDate"
              disabled={isSubmitting || !currContractEnabled}
            />
          </FormField>

          <FormField label={t("page.integration.wunderbricks.form.instance-url")} required={anyEnabled}>
            <FormInput<WunderbricksFormValues, "instanceUrl">
              name="instanceUrl"
              rules={{
                validate: {
                  required: (value, formValues) => {
                    if (formValues.ticketsEnabled || formValues.contractsEnabled) {
                      return createRequiredStringRule(t, "page.integration.wunderbricks.form.instance-url")(
                        value,
                        formValues,
                      );
                    }
                  },
                },
              }}
              placeholder="https://your-instance.wunderbricks.com"
              disabled={isSubmitting || !anyEnabled}
            />
          </FormField>

          <FormField label={t("page.integration.wunderbricks.form.client-id")} required={anyEnabled}>
            <FormInput<WunderbricksFormValues, "clientId">
              name="clientId"
              rules={{
                validate: {
                  required: (value, formValues) => {
                    if (formValues.ticketsEnabled || formValues.contractsEnabled) {
                      return createRequiredStringRule(t, "page.integration.wunderbricks.form.client-id")(
                        value,
                        formValues,
                      );
                    }
                  },
                },
              }}
              disabled={isSubmitting || !anyEnabled}
            />
          </FormField>

          <FormField label={t("page.integration.wunderbricks.form.client-secret")} required={anyEnabled}>
            <FormInput<WunderbricksFormValues, "clientSecret">
              name="clientSecret"
              type="password"
              rules={{
                validate: {
                  required: (value, formValues) => {
                    if (formValues.ticketsEnabled || formValues.contractsEnabled) {
                      return createRequiredStringRule(t, "page.integration.wunderbricks.form.client-secret")(
                        value,
                        formValues,
                      );
                    }
                  },
                },
              }}
              disabled={isSubmitting || !anyEnabled}
            />
          </FormField>

          <FormField label={t("page.integration.wunderbricks.form.requester-user")} required={anyEnabled}>
            <div className="max-w-search-wide">
              <FormSelect<WunderbricksFormValues, UserDto>
                items={adminUsers}
                keySelector={(user) => user.id}
                renderOption={(user) => `${user.firstName} ${user.lastName}`}
                name="requesterUser"
                rules={{
                  validate: (value) =>
                    anyEnabled && !value
                      ? t("components.form.error.required", {
                          inputName: t("page.integration.wunderbricks.form.requester-user"),
                        })
                      : undefined,
                }}
                disabled={isSubmitting || !anyEnabled}
              />
            </div>
          </FormField>

          {currTicketEnabled && (
            <Accordion.Root>
              <Accordion.Item value="status-mappings">
                <Accordion.Trigger>{t("page.integration.wunderbricks.form.status-mappings")}</Accordion.Trigger>
                <Accordion.Content>
                  {statusMappingFeedback}
                  {canEditTicketStatusMapping && (
                    <Stack gap="sm">
                      <Inline gap="sm" align="center">
                        <span className="flex-1 text-caption text-text-secondary">
                          {t("page.integration.wunderbricks.form.status-mappings.wunderbricks-status")}
                        </span>
                        <span className="flex-1 text-caption text-text-secondary">
                          {t("page.integration.wunderbricks.form.status-mappings.aop-status")}
                        </span>
                      </Inline>
                      {ticketStatusOptions.externalStatuses.map((ticketExternalStatus) => (
                        <Inline
                          key={ticketExternalStatus.value}
                          gap="sm"
                          align="center"
                          data-testid={`wunderbricks-status-${ticketExternalStatus.value}`}
                        >
                          <label
                            className="flex-1"
                            htmlFor={`wunderbricks-${statusMappingKey(ticketExternalStatus.value)}`}
                          >
                            <Text>{ticketExternalStatus.label}</Text>
                          </label>
                          <div className="flex-1">
                            <FormSelect<WunderbricksFormValues, WunderbricksAopTicketStatusOptionDto>
                              items={ticketStatusOptions.aopStatuses}
                              keySelector={(status) => status.id}
                              renderOption={(status) => (
                                <TicketStatus description={status.label} labelColor={status.color} variant="dot" />
                              )}
                              renderSelected={(status) => (
                                <TicketStatus description={status.label} labelColor={status.color} variant="dot" />
                              )}
                              name={`statusMappings.${statusMappingKey(ticketExternalStatus.value)}`}
                              id={`wunderbricks-${statusMappingKey(ticketExternalStatus.value)}`}
                              emptyItem={t("page.integration.wunderbricks.form.status-mappings.none")}
                              disabled={isSubmitting}
                            />
                          </div>
                        </Inline>
                      ))}
                    </Stack>
                  )}
                </Accordion.Content>
              </Accordion.Item>
            </Accordion.Root>
          )}
        </Form.Body>
        <Form.Footer
          renderPrimaryAction={() => (
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.action.save")}
            </Button>
          )}
        />
      </Form.Container>
    </Form.Root>
  );
}
